import { cn } from "./ui/utils";

type StatusVariant =
  | "pass" | "fail" | "warning" | "info"
  | "open" | "closed" | "in-progress" | "draft"
  | "active" | "inactive" | "pending" | "resolved"
  | "high" | "medium" | "low" | "critical"
  | "in_review" | "approved" | "rejected" | "published" | "superseded" | "withdrawn" | "review_overdue" | "retired" | "in_progress";

const variantStyles: Record<StatusVariant, { chip: string; dot: string }> = {
  pass:        { chip: "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-1 dark:ring-emerald-500/30",      dot: "bg-emerald-500" },
  fail:        { chip: "bg-red-50 text-red-800 dark:bg-red-500/20 dark:text-red-300 dark:ring-1 dark:ring-red-500/30",                 dot: "bg-red-500" },
  warning:     { chip: "bg-amber-50 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",            dot: "bg-amber-500" },
  info:        { chip: "bg-sky-50 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300 dark:ring-1 dark:ring-sky-500/30",                 dot: "bg-sky-500" },
  open:        { chip: "bg-red-50 text-red-800 dark:bg-red-500/20 dark:text-red-300 dark:ring-1 dark:ring-red-500/30",                 dot: "bg-red-500" },
  closed:      { chip: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30",          dot: "bg-slate-400" },
  "in-progress": { chip: "bg-sky-50 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300 dark:ring-1 dark:ring-sky-500/30",              dot: "bg-sky-500" },
  draft:       { chip: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400 dark:ring-1 dark:ring-slate-500/30",    dot: "bg-slate-400" },
  active:      { chip: "bg-indigo-50 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-400 dark:ring-1 dark:ring-indigo-500/30",   dot: "bg-indigo-500" },
  inactive:    { chip: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30",          dot: "bg-slate-400" },
  pending:     { chip: "bg-amber-50 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",           dot: "bg-amber-500" },
  resolved:    { chip: "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-1 dark:ring-emerald-500/30",     dot: "bg-emerald-500" },
  critical:    { chip: "bg-red-50 text-red-800 dark:bg-red-500/20 dark:text-red-300 dark:ring-1 dark:ring-red-500/30",                 dot: "bg-red-500" },
  high:        { chip: "bg-orange-50 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300 dark:ring-1 dark:ring-orange-500/30",        dot: "bg-orange-500" },
  medium:      { chip: "bg-amber-50 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",           dot: "bg-amber-500" },
  low:         { chip: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30",          dot: "bg-slate-400" },
  /* Lifecycle statuses */
  in_review:      { chip: "bg-blue-50 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400 dark:ring-1 dark:ring-blue-500/30",                    dot: "bg-blue-500" },
  approved:       { chip: "bg-violet-50 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300 dark:ring-1 dark:ring-violet-500/30",         dot: "bg-violet-500" },
  rejected:       { chip: "bg-red-50 text-red-800 dark:bg-red-500/20 dark:text-red-300 dark:ring-1 dark:ring-red-500/30",               dot: "bg-red-500" },
  published:      { chip: "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-1 dark:ring-emerald-500/30",    dot: "bg-emerald-500" },
  superseded:     { chip: "bg-orange-50 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300 dark:ring-1 dark:ring-orange-500/30",       dot: "bg-orange-500" },
  withdrawn:      { chip: "bg-rose-50 text-rose-900 dark:bg-rose-500/20 dark:text-rose-300 dark:ring-1 dark:ring-rose-500/30",             dot: "bg-rose-700" },
  review_overdue: { chip: "bg-amber-50 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",         dot: "bg-amber-500" },
  retired:        { chip: "bg-rose-50 text-rose-900 dark:bg-rose-500/20 dark:text-rose-300 dark:ring-1 dark:ring-rose-500/30",             dot: "bg-rose-700" },
  in_progress:    { chip: "bg-amber-50 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",           dot: "bg-amber-500" },
};

/* Two sizes only, so a status chip reads the same everywhere it appears:
 * - "md" (default) keeps the wider fixed-width chip existing screens use.
 * - "sm" is the compact indicator pill for dense surfaces (cards, tables):
 *   no min-width, tighter padding, so it sits as a label rather than a
 *   tappable-looking block. */
const sizeStyles: Record<"sm" | "md", string> = {
  sm: "px-2 py-[3px] text-[10px] leading-none tracking-normal",
  md: "min-w-[110px] px-3 py-0.5 text-[11px]",
};

interface StatusBadgeProps {
  status: StatusVariant;
  label?: string;
  dot?: boolean;
  size?: "sm" | "md";
  className?: string;
}

export function StatusBadge({ status, label, dot = true, size = "md", className = "" }: StatusBadgeProps) {
  const safeStatus = status ?? "info";
  const v = variantStyles[safeStatus] ?? variantStyles.info;
  const defaultLabel = safeStatus === "retired" ? "Withdrawn" : safeStatus.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const displayLabel = label ?? defaultLabel;

  return (
    <span
      // cn() so a caller's className can actually override the defaults
      // (e.g. a card passing a narrower min-width) instead of both rules
      // landing in the stylesheet and the winner depending on source order.
      className={cn(
        "inline-flex items-center justify-center rounded-full font-bold whitespace-nowrap",
        sizeStyles[size] ?? sizeStyles.md,
        v.chip,
        className,
      )}
    >
      {displayLabel}
    </span>
  );
}
